import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import {
  MatLegacySelect as MatSelect,
  MatLegacySelectChange as MatSelectChange,
} from '@angular/material/legacy-select';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BehaviorSubject, debounceTime, noop, switchMap, tap } from 'rxjs';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { Moment } from 'moment';
import {
  CURRENT_USER_FILTER_LABEL,
  CURRENT_USER_FILTER_TOKEN,
  FilterListItemChangeArgs,
  FilterListItemModel,
  FilterListItemValueModel,
  LookupContract,
  LookupResult,
  Operator,
  OperatorDisplayMap,
} from '../cross-cutting/models';

@Component({
  selector: 'ts-filter-list-item',
  templateUrl: './ts-filter-list-item.component.html',
  styleUrls: ['./ts-filter-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TsFilterListItemComponent extends OnDestroyMixin implements OnInit {
  @Input() index = -1;
  @Input() fields: FilterListItemModel[];
  @Input() enableFormEvent = true;
  @Input() set filter(value: FilterListItemValueModel) {
    this.filterValue = value;
    const fieldInfo = this.fields.find(({ name }) => name === value.fieldName);

    if (fieldInfo?.type === 'record' && value.operator !== 'is') {
      this.selectedLookupResults = (value.value ?? []).map(([id, label]) => ({ id, label }));
      this.form.patchValue(
        {
          ...value,
          value: this.selectedLookupResults.map(({ id }) => id),
        },
        { emitEvent: this.enableFormEvent },
      );
    } else {
      this.form.patchValue(value, { emitEvent: this.enableFormEvent });
    }
  }
  @Input() lookupContract: LookupContract;
  @Input() hasGroups = true;
  @Input() displayFieldSelection = true;
  @Input() valueBackgroundColor: string = '';
  @Input() isValueBold = false;

  @Output() filterDefChanged = new EventEmitter<FilterListItemChangeArgs>();
  @Output() filterRemoved = new EventEmitter<number>();

  filterValue: FilterListItemValueModel;
  form = new FormGroup({
    fieldName: new FormControl(null as string),
    operator: new FormControl(null as Operator),
    value: new FormControl(null),
  });
  recordSearch = new FormControl();
  lookupLoading = new BehaviorSubject<boolean>(false);
  lookupResults = new BehaviorSubject<LookupResult[]>([]);
  selectedLookupResults: LookupResult[] = [];
  searchTxt = '';

  private get fieldNameControl(): AbstractControl {
    return this.form.get('fieldName');
  }

  get operator(): AbstractControl {
    return this.form.get('operator');
  }

  get value(): AbstractControl {
    return this.form.get('value');
  }

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.setupFormChangesListeners();
  }

  onValueInputBlur(event: Event & { target: { value: string } }): void {
    const field = this.fields.find((x) => x.name === this.filterValue.fieldName);

    if (field.name === 'id' || field.name.endsWith('.id')) {
      const valueAsArray = JSON.parse(JSON.stringify(event.target.value.split(','))).filter((x) => x);
      this.updateFilterValue(valueAsArray);
      return;
    }

    if (field?.type === 'money') {
      this.updateFilterValue(this.form.get('value').value);
      return;
    }

    this.updateFilterValue(event.target.value);
  }

  onValueSelectBlur(select: MatSelect): void {
    if (this.filterValue.operator === 'in' && !Array.isArray(select.value)) {
      this.updateFilterValue([select.value]);
      return;
    }

    if (this.fields.find(({ name }) => name === this.filterValue.fieldName)?.type === 'record') {
      this.updateFilterValue(this.selectedLookupResults.map(({ id, label }) => [id, label]));
      return;
    }

    this.updateFilterValue(select.value);
  }

  onValueDateBlur(event: MatDatepickerInputEvent<Moment>): void {
    this.updateFilterValue(event.value.format('YYYY-MM-DD'));
  }

  onValueSelectOpen(select: MatSelect): void {
    this.recordSearch.setValue('');
  }

  readonly currentUserToken = CURRENT_USER_FILTER_TOKEN;
  readonly currentUserLabel = CURRENT_USER_FILTER_LABEL;

  /** The special "Current User" value is offered on user fields: a record of type User, or a select of users. */
  showCurrentUserOption(field: FilterListItemModel): boolean {
    return (
      (field?.type === 'record' && field.meta.record_type === 'User') ||
      (field?.type === 'select' && field.meta.option_name === 'users')
    );
  }

  /** The token has its own dedicated option, so a selected entry for it must not be listed a second time. */
  isCurrentUserEntry(field: FilterListItemModel, id: number | string): boolean {
    return id === this.currentUserToken && this.showCurrentUserOption(field);
  }

  onValueSelectChange(event: MatSelectChange): void {
    const field = this.fields.find(({ name }) => name === this.filterValue.fieldName);
    // the token never comes from the lookup, so it is a selectable result of its own on user fields
    const candidates = this.showCurrentUserOption(field)
      ? [...this.lookupResults.value, { id: this.currentUserToken, label: this.currentUserLabel }]
      : this.lookupResults.value;

    this.selectedLookupResults = candidates
      .filter(({ id }) => event.value.map((y) => y).includes(id))
      .reduce((p, c) => (!p.find(({ id }) => id === c.id) ? [...p, c] : p), []);
  }

  getMaskForNumberDecimal(field: FilterListItemModel): string {
    if (!field.meta.scale) {
      return '0*';
    }

    return `0*.${Array(field.meta.scale).fill(0).join('')}`;
  }

  mutateOperatorListFn(value: Operator[]): { name: Operator; label: string }[] {
    if (!value) {
      return [];
    }

    const operators = [...value];
    if (!operators.includes('is')) {
      operators.push('is');
    }

    return operators.map((x) => ({ name: x, label: OperatorDisplayMap[x] }));
  }

  innerFilterFn(options: any[], filter: any): any[] {
    if (filter?.innerFilter) {
      return filter.innerFilter(options);
    }

    return options;
  }

  private updateFilterValue(value: any): void {
    this.filterDefChanged.emit({ ...this.filterValue, index: this.index, value });
  }

  private setupFormChangesListeners(): void {
    this.fieldNameControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        tap((fieldName: string) => {
          if (fieldName !== this.filterValue.fieldName) {
            this.filterDefChanged.emit({
              fieldName,
              operator: this.fields.find((x) => x.name === fieldName)?.meta.filtering.operators[0],
              index: this.index,
              value: null,
            });
          }
        }),
      )
      .subscribe(noop);

    this.operator.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        tap((operator: Operator) => {
          // the `is` operator renders a value select (Empty / Not Empty), so the control must stay enabled
          this.value.enable();

          this.filterDefChanged.emit({ ...this.filterValue, value: null, operator, index: this.index });
        }),
      )
      .subscribe(noop);

    this.value.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        tap((value: any) => {
          const field = this.fields.find((x) => x.name === this.filterValue.fieldName);
          const isDateFunctionField =
            (field?.type === 'date' || field?.type === 'datetime') && this.operator.value === 'is';
          const isEmptyOperatorField =
            this.operator.value === 'is' && field?.type !== 'checkbox' && !isDateFunctionField;

          if (isDateFunctionField || isEmptyOperatorField) {
            this.updateFilterValue(value);
          }
        }),
      )
      .subscribe(noop);

    this.recordSearch.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        debounceTime(300),
        tap(() => this.lookupLoading.next(true)),
        switchMap((value: string) => {
          const fieldInfo = this.fields.find(({ name }) => name === this.filterValue.fieldName);
          return this.lookupContract.lookFor({ query: value, relatedTo: fieldInfo.meta.record_type });
        }),
        tap((result: LookupResult[]) => {
          this.lookupResults.next([...this.selectedLookupResults, ...result]);
          this.lookupLoading.next(false);
        }),
      )
      .subscribe(noop);
  }

  hideSelected(value: LookupResult[], selectedLookupResults: LookupResult[]): LookupResult[] {
    return value.filter(({ id }) => !selectedLookupResults.map((result) => result.id).includes(id));
  }

  transformDateFunctions(functions: string[]): { value: string; label: string }[] {
    if (!functions || !Array.isArray(functions)) {
      return [];
    }

    return functions.map((func) => ({
      value: func,
      label: func,
    }));
  }

  isOperatorIs(): boolean {
    return this.operator.value === 'is';
  }

  getEmptyOptions(): { value: string; label: string }[] {
    return [
      { value: '__empty__', label: 'Empty' },
      { value: '__not_empty__', label: 'Not Empty' },
    ];
  }

  getDateOptionsWithEmpty(functions: string[]): { value: string; label: string }[] {
    const dateFunctions = this.transformDateFunctions(functions);
    const emptyOptions = this.getEmptyOptions();
    return [...dateFunctions, ...emptyOptions];
  }
}
